import { Link } from "gatsby"
import React, { useEffect, useCallback } from "react"
import { LinksProp } from "@wp/ui"
import styled, { StyledComponent } from "styled-components"
import Logo from "../../img/logo.inline.svg"
import Icon from "@mdi/react"
import { mdiArrowTopRight } from "@mdi/js"

const HeaderElement: StyledComponent<"header", any, any> = styled.header`
  background: #f9f9f9;
  display: grid;
  grid-template-columns: 13rem 1fr 13rem;
  grid-template-areas: "logo nav purchase";
  padding: 0 4vw;
  height: 80px;
  position: sticky;
  width: calc(100% - 8vw);
  z-index: 999;
  left: 0;
  top: 0;
  transition: background 0.35s ease;

  .purchase {
    grid-area: purchase / purchase;
    border: none;
    position: relative;
    outline: none;
    background: transparent;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    cursor: pointer;

    svg {
      box-sizing: border-box;
      height: 1.28rem;
      width: 1.28rem;
    }
  }

  a {
    position: relative;
    
    &:hover,
    &.active,
    &:focus {
      color: #999;
      text-decoration: none;
    }
  }

  nav {
    grid-area: nav;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      width: 11rem;
    }

    &:not(#mobile-nav) a.active::after {
      content: "";
      left: 0;
      position: absolute;
      width: 100%;
      height: 1px;
      background: #ccc;
      bottom: -4px;
    }

    ul {
      li {
        display: block;

        &:not(:last-child) {
          margin-right: 20px;
        }
      }

      &.page-links {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .logo {
    grid-area: logo / logo;
    border: none;
    display: flex;
    position: relative;
    border-bottom: none;
    align-items: center;

    a {
      position: absolute;

      svg {
        width: 11rem;
      }
    }
  }

  @media only screen and (max-width: 768px) {
    padding: 0 0px;
    height: 65px;
    grid-template-columns: 65px 1fr 65px;
    width: 100%;
    grid-template-areas: "logo logo logo";

    .logo {
      justify-content: center;
    }

    nav,
    .purchase {
      display: none;
    }
  }
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PurchaseButton: StyledComponent<"a", any, any> = styled.a`
  padding: 14px 42px;
  background: #333;
  color: #fff;
  text-transform: uppercase;
  font-size: 10px;
  text-align: center;
  letter-spacing: 1px;
  font-family: "Gordita Regular", "Helvetica Neue";
  cursor: pointer;
  outline: none;
  transition: box-shadow 0.35s, background 0.35s ease-in-out;

  &:hover,
  &:focus {
    background: #000;
    color: #fff !important;
    box-shadow: 0 5px 15px -5px #000000aa;
  }

  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    margin-left: 7px;
  }
`

export type HeaderComponent = {
  links: LinksProp[]
}

const Header: React.FC<HeaderComponent> = ({ links }) => {
  const hideHeader = useCallback(() => {
    const scrollTopTrigger: number = document
        .querySelector("main")
        .getBoundingClientRect().top,
      header: HTMLElement = document.querySelector("header")
    if (header) {
      if (scrollTopTrigger - 65 < 0) {
        header.style.background = "#fff"
        header.style.borderBottom = "1px solid #eaeaea"
      } else {
        header.style.background = "#f9f9f9"
        header.style.borderBottom = "none"
      }
    }
  }, [])

  useEffect(() => {
    window.addEventListener("scroll", hideHeader)
    window.addEventListener("load", hideHeader)
    return (): void => {
      window.removeEventListener("scroll", hideHeader)
      window.removeEventListener("load", hideHeader)
    }
  }, [])

  return (
    <HeaderElement>
      <div className="logo menu">
        <Link to="/" className="main-logo">
          <Logo />
        </Link>
      </div>
      {/* main nav */}
      <nav>
        <ul className="page-links">
          {links.map((link: LinksProp, idx: number) => (
            <li key={idx}>
              <Link
                activeClassName="active"
                className="font-head font-xs"
                to={link.to || link.name}
              >
                {link.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="purchase">
        <PurchaseButton
          href="https://"
          target="_blank"
          rel="noreferrer noopener"
        >
          Purchase
          <Icon path={mdiArrowTopRight} color="#fff" size={0.85} />
        </PurchaseButton>
      </div>
    </HeaderElement>
  )
}

export default Header
